import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Camera,
  Crown,
  Eye,
  GraduationCap,
  Handshake,
  Link2,
  Mail,
  MapPin,
  Palette,
  Share2,
  ShoppingBag,
  Sparkles,
  TicketPercent,
  Video,
} from "lucide-react";
import type { CreatorLink, CreatorProfile } from "../lib/api";
import { classNames, formatNumber, formatScore } from "../lib/format";
import { profileThemeVars } from "../lib/profile-themes";
import { NETWORK_KEYS, type NetworkKey } from "../lib/networks";
import { NetworkIcon } from "./brand-icons";
import { Avatar, Breadcrumbs, Button, Card, Eyebrow, ScoreRing } from "./ui";
import { profileStyles, publicStyles } from "./public-classes";

export function publicProfileTheme(preset?: string | null): CSSProperties {
  return profileThemeVars(preset);
}

const LINK_TYPE_ICONS: Record<string, typeof Link2> = {
  product: ShoppingBag,
  coupon: TicketPercent,
  course: GraduationCap,
  portfolio: Palette,
  contact: Mail,
  partnership: Handshake,
  // Values from the icon picker this list replaced, so links saved earlier keep
  // the glyph their owner chose.
  sparkles: Sparkles,
  video: Video,
  camera: Camera,
};

/** Showing the destination host is what separates a real link list from a wall
 *  of unlabelled buttons — the visitor sees where the click actually goes. */
function linkHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] || url;
  }
}

export function ProfileLinkIcon({ link }: { link: CreatorLink }) {
  // A network link still wins: its official glyph names the destination better
  // than any type could. Networks are no longer offered when creating a link,
  // but the ones already saved keep working.
  if (link.platform && NETWORK_KEYS.includes(link.platform as NetworkKey))
    return <NetworkIcon network={link.platform as NetworkKey} size={20} />;
  const Icon = link.icon ? LINK_TYPE_ICONS[link.icon] : undefined;
  return Icon ? <Icon /> : <Link2 />;
}

export interface PublicProfileViewProps {
  creator: CreatorProfile;
  /**
   * `preview` renders the same markup for the dashboard, minus what belongs to
   * a visitor rather than to the creator: no tracking, no report, no navigation
   * away from the editor. Sharing one component is what keeps the preview
   * honest — a second copy would drift the moment the public page changes.
   */
  variant?: "public" | "preview";
  onLinkClick?: (link: CreatorLink) => void;
  onShare?: () => void;
  shareStatus?: string;
  onReport?: () => void;
}

export function PublicProfileView({
  creator,
  variant = "public",
  onLinkClick,
  onShare,
  shareStatus,
  onReport,
}: PublicProfileViewProps) {
  const preview = variant === "preview";
  const activeLinks = creator.links.filter((link) => link.active !== false);
  // Networks become a compact icon row under the name and the creator's own
  // links get the full-width buttons. Mixing both in one list buried whatever
  // the person actually wanted clicked among their social handles.
  const socialLinks = activeLinks.filter(
    (link) => link.platform && link.platform !== "CUSTOM" && NETWORK_KEYS.includes(link.platform as NetworkKey),
  );
  const buttonLinks = activeLinks.filter((link) => !socialLinks.includes(link));
  const breadcrumbs = [
    { label: "Início", ...(preview ? {} : { to: "/" }) },
    {
      label: creator.category,
      ...(preview ? {} : { to: `/categoria/${creator.categorySlug}` }),
    },
    { label: creator.name },
  ];

  return (
    <>
      <div className={profileStyles.cover}>
        <div className={profileStyles.coverPattern} />
      </div>
      {/* No preview-only padding here: publicStyles.shell already reserves the
          side margin, and adding px-4 on top of it doubled the gutter, which is
          why the preview looked inset compared to the published page. */}
      <section className={classNames(publicStyles.shell, profileStyles.shell)}>
        <Breadcrumbs items={breadcrumbs} />
        <div
          className={classNames(profileStyles.header, profileStyles.headerAvatar)}
        >
          <Avatar name={creator.name} src={creator.avatarUrl} size="xl" />
          <div className={profileStyles.identity}>
            <div className={profileStyles.nameLine}>
              <h1>{creator.name}</h1>
              {creator.isVerified && (
                <BadgeCheck aria-label="Perfil verificado" />
              )}
              {creator.isPremium && (
                <span className={profileStyles.premiumBadge}>
                  <Crown size={13} /> Premium
                </span>
              )}
            </div>
            <p>{creator.headline}</p>
            <div className={profileStyles.meta}>
              <span>
                <MapPin size={15} /> {creator.city}, {creator.state}
              </span>
              <span>{creator.category}</span>
            </div>
            {socialLinks.length > 0 && (
              <div className={profileStyles.socials}>
                {socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className={profileStyles.socialLink}
                    aria-label={link.title}
                    title={link.title}
                    onClick={() => onLinkClick?.(link)}
                  >
                    <ProfileLinkIcon link={link} />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className={profileStyles.actions}>
            <Button
              onClick={() => onShare?.()}
              variant="secondary"
              disabled={preview}
            >
              <Share2 size={17} /> Compartilhar
            </Button>
            {shareStatus && <span role="status">{shareStatus}</span>}
          </div>
        </div>
        <div
          className={classNames(profileStyles.layout, preview && "!my-8")}
        >
          <div className={profileStyles.content}>
            <div className={profileStyles.linktree}>
              <div className={profileStyles.contentHeading}>
                <div>
                  <Eyebrow>Meus links</Eyebrow>
                  <h2>Vamos nos conectar</h2>
                </div>
                <span className={profileStyles.linktreeCount}>
                  <Link2 /> {buttonLinks.length}
                </span>
              </div>
              {buttonLinks.length ? (
                <div className={profileStyles.linktreeList}>
                  {buttonLinks.map((link) => {
                    const on = Boolean(link.isHighlighted);
                    return (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className={classNames(profileStyles.profileLink, on && profileStyles.profileLinkHighlighted, preview && "pointer-events-auto")}
                        onClick={() => onLinkClick?.(link)}
                      >
                        <span className={classNames(profileStyles.profileLinkIcon, on && profileStyles.profileLinkIconOn)}>
                          <ProfileLinkIcon link={link} />
                        </span>
                        <span className={profileStyles.profileLinkCopy}>
                          <strong className={classNames(profileStyles.profileLinkTitle, on && profileStyles.profileLinkTitleOn)}>
                            {link.title}
                          </strong>
                          <span className={classNames(profileStyles.profileLinkHost, on && profileStyles.profileLinkHostOn)}>
                            {linkHost(link.url)}
                          </span>
                          {on && (
                            <span className={profileStyles.profileLinkTag}>
                              <Sparkles /> Destaque
                            </span>
                          )}
                        </span>
                        <span className={classNames(profileStyles.profileLinkGo, on && profileStyles.profileLinkGoOn)} aria-hidden="true">
                          <ArrowUpRight />
                        </span>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className={profileStyles.linktreeEmpty}>
                  <Link2 aria-hidden="true" />
                  <strong>Nenhum link publicado ainda</strong>
                  <span>Os links adicionados no painel aparecem aqui.</span>
                </div>
              )}
            </div>
            <Card className={profileStyles.about}>
              <Eyebrow>Sobre</Eyebrow>
              <h2>Prazer, eu sou {creator.name.split(" ")[0]}.</h2>
              <p>{creator.bio}</p>
              <div className={profileStyles.tags}>
                {creator.tags?.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </Card>
          </div>
          <aside className={profileStyles.aside}>
            <Card className={profileStyles.scoreCard}>
              <div className={profileStyles.scoreHead}>
                <div>
                  <Eyebrow tone="lime">Creator Score</Eyebrow>
                  <h2>Presença em movimento</h2>
                </div>
                <ScoreRing value={creator.score} size={128} />
              </div>
              {creator.scoreBreakdown &&
                Object.keys(creator.scoreBreakdown).length > 0 && (
                <div className={profileStyles.factors}>
                  {Object.entries(creator.scoreBreakdown).map(([key, value]) => (
                    <div key={key}>
                      <span>
                        {
                          (
                            {
                              completeness: "Completude",
                              engagement: "Engajamento",
                              discovery: "Descoberta",
                              activity: "Atividade",
                            } as Record<string, string>
                          )[key]
                        }
                      </span>
                      <strong>{formatScore(value)}</strong>
                      <i
                        style={{
                          width: `${Math.min(100, value * (key === "activity" ? 10 : 3.33))}%`,
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {!preview && (
                <Link to="/sobre#score">
                  Entenda este score <ArrowRight size={15} />
                </Link>
              )}
            </Card>
            {creator.showPublicViewCount && (
              <Card className={profileStyles.proof}>
                <Eye />
                <div>
                  <strong>{formatNumber(creator.views || 0)}</strong>
                  <span>visitas ao perfil</span>
                </div>
              </Card>
            )}
            {!preview && (
              <div className={profileStyles.report}>
                <button onClick={() => onReport?.()}>Denunciar perfil</button>
                <small>Última atualização recente</small>
              </div>
            )}
          </aside>
        </div>
      </section>
    </>
  );
}
